/**
 * Skeleton ontology — the versioned data contract for the canonical hub (ADR-003 §2a).
 * Depends on: zod, ./errors. Depended on by: mapping, retarget, solve, hands.
 *
 * The ontology is LOADED as data and passed in — never imported as a global
 * singleton (ADR-003 §4). loadOntology() is the only door: schema validation
 * first, then semantic integrity checks; both fail loudly with every problem.
 */
import { z } from 'zod';
import { OntologyIntegrityError, SchemaValidationError } from './errors.js';

export const vec3Schema = z.object({ x: z.number(), y: z.number(), z: z.number() });
export type Vec3 = z.infer<typeof vec3Schema>;

export const jointTypeSchema = z.enum(['ball', 'hinge', 'universal']);
export type JointType = z.infer<typeof jointTypeSchema>;

export const boneSideSchema = z.enum(['left', 'right', 'center']);
export type BoneSide = z.infer<typeof boneSideSchema>;

export const boneRegionSchema = z.enum([
  'spine',
  'head',
  'breast',
  'armL',
  'armR',
  'handL',
  'handR',
  'legL',
  'legR',
  /** 3.4.0 (ADR-031 D6): the optional grafted tail chain under the pelvis. */
  'tail',
  /** 3.5.0 (ADR-038 J2): the optional grafted soft-tissue leaves (breast, belly, glute); 3.6.0 the hair's (ADR-039 T2a). */
  'softTissue',
]);
export type BoneRegion = z.infer<typeof boneRegionSchema>;

/** Swing-twist limits in DEGREES, as the BBSA spec states them. */
export const swingTwistLimitsSchema = z.object({
  swingDeg: z.number().min(0).max(360),
  twistMinDeg: z.number(),
  twistMaxDeg: z.number(),
});
export type SwingTwistLimits = z.infer<typeof swingTwistLimitsSchema>;

export const ontologyBoneSchema = z.object({
  /** Canonical ID — BBSA PascalCase (ADR-003 §2a). */
  id: z.string().min(1),
  /** Parent canonical ID; null only for the root. */
  parent: z.string().nullable(),
  region: boneRegionSchema,
  side: boneSideSchema,
  /** Explicit mirror link; null for center bones. First-class (ADR-003 §2a). */
  mirror: z.string().nullable(),
  /**
   * Joint model + limits; null for leaf/tip markers and unconstrained joints
   * (an explicit null, never an absent field — a deliberate "unconstrained"
   * must be distinguishable from forgotten data).
   */
  joint: z.object({ type: jointTypeSchema, limits: swingTwistLimitsSchema.nullable() }).nullable(),
  /** True for *_End / fingertip leaf markers: they terminate chains, never articulate. */
  tip: z.boolean(),
  /**
   * Unit direction this bone points (joint → child) in the BBSA zero pose —
   * right-handed, +Y up, +Z forward, arms ±X (ADR-003 §2a). Null for tips.
   */
  zeroPoseDirection: vec3Schema.nullable(),
  /**
   * Unit direction PERPENDICULAR to zeroPoseDirection completing the bone's
   * semantic frame in the zero pose — the twist/reference axis. A single
   * child direction fixes only 2 of 3 rotational DOF; this closes the third
   * (ADR-003 §11, accepted rest-pose-reorientation proposal). Null for tips.
   */
  zeroPoseSecondary: vec3Schema.nullable(),
  /**
   * The bone-local axis negated by the rig-mirroring convention — the parity
   * convention C in Rt = M·Rs·C (ADR-003 §11, accepted reflection proposal).
   * Required on sided bones, null on center bones.
   */
  parityAxis: vec3Schema.nullable(),
  /**
   * Whether this bone's child direction reflects its OWN rotational frame.
   * False for the clavicle class: reorientation must refuse rather than read
   * a frame from the upper-arm direction (ADR-003 §11). Required so "not
   * stated" can never masquerade as "informative".
   */
  childDirectionInformative: z.boolean(),
  /**
   * The child bone zeroPoseDirection points at (ADR-003 §11) — resolves the
   * "which child defines the direction" ambiguity on multi-child bones
   * (Hips, Spine2, Hand). Null for tips and for childless articulating
   * bones (Jaw, Eyes).
   */
  primaryChild: z.string().nullable(),
  /**
   * Whether absolute transport (ADR-019) must ANCHOR this bone's rest:
   * source-rest plays as target-rest, with source MOTION transported as
   * world-faithful deltas on top. True for the clavicle class, whose rest
   * direction is per-rig ANATOMY (a real clavicle slopes ~10° up to the
   * acromion; Mixamo's is horizontal — same neutral shoulders), so matching
   * the source's world direction transplants the source's shoulder-girdle
   * geometry onto the target: measured 2026-08-09, every clip depressed the
   * Tripo shoulder 10–17° and the bled clavicle weights collapsed the chest
   * (chest-collapse forensics). Defaults false = the plain absolute law.
   */
  restAnchoredTransport: z.boolean().default(false),
  /**
   * Whether absolute transport (ADR-019 §2.2) must anchor only this bone's
   * TWIST: at source-rest the bone keeps its own bind roll about its axis
   * (the receiver keeps its nail), while its swing still transports
   * world-faithfully and its children RIDE the anchor rather than cancel it
   * (a digit is one nail — phalanges do not twist at hinge joints). True for
   * the thumb chain: a thumb's roll about its metacarpal is the rig's
   * opposition CONVENTION, not pose — measured 2026-08-25, Mixamo's rest
   * roll transported onto a Tripo graft rolled the metacarpal 48–82° from
   * the character's own bind before any motion (rest-roll census). Mutually
   * exclusive with restAnchoredTransport. Defaults false.
   */
  twistAnchoredTransport: z.boolean().default(false),
  /**
   * Whether this bone's CHILDREN ride its anchor (rest or twist) instead of
   * cancelling it in their prefix. The clavicle's arm cancels (the arm's
   * world pose is the pose — it must not ride the shoulder correction); a
   * digit rides (a thumb is one thing — a phalanx that cancelled its
   * metacarpal's anchor would re-introduce the source's rest at the
   * knuckle). Measured 2026-08-25: under plain transport Jay's thumb
   * metacarpal turned 26–38° and its knuckle moved 47–65 mm at source-REST
   * (Mixamo's splayed T-pose thumb vs a relaxed modelled one — convention,
   * not pose), reading as a thumb twice its length. Requires an anchor.
   */
  anchorChildrenRide: z.boolean().default(false),
});
export type OntologyBone = z.infer<typeof ontologyBoneSchema>;

export const ontologySchema = z.object({
  /** Semver of the ontology CONTENT (ADR-003 §6 rules). */
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /**
   * The plane every `mirror` link is defined against (ADR-003 §11) —
   * load-bearing for reflection classification, so declared, not implied.
   */
  mirrorPlane: z.object({ normal: vec3Schema, offset: z.number() }),
  /** Named, closed bone sets rigs validate against (ADR-003 §2a). */
  profiles: z.record(z.string(), z.array(z.string()).min(1)),
  bones: z.array(ontologyBoneSchema).min(1),
});
export type OntologyData = z.infer<typeof ontologySchema>;

export interface Ontology extends OntologyData {
  readonly byId: ReadonlyMap<string, OntologyBone>;
}

function checkHierarchy(data: OntologyData, ids: Map<string, OntologyBone>, out: string[]): void {
  const roots = data.bones.filter((b) => b.parent === null);
  if (roots.length !== 1) {
    out.push(`expected exactly one root bone, found ${roots.length}`);
  }
  for (const b of data.bones) {
    if (b.parent !== null && !ids.has(b.parent)) {
      out.push(`bone "${b.id}" has unknown parent "${b.parent}"`);
    }
    if (b.parent === b.id) out.push(`bone "${b.id}" is its own parent`);
  }
  // cycle check via parent-walk with visited budget
  for (const b of data.bones) {
    let cur: OntologyBone | undefined = b;
    let steps = 0;
    while (cur && cur.parent !== null && steps <= data.bones.length) {
      cur = ids.get(cur.parent);
      steps += 1;
    }
    if (steps > data.bones.length) out.push(`cycle in ancestry of bone "${b.id}"`);
  }
}

function checkMirrors(data: OntologyData, ids: Map<string, OntologyBone>, out: string[]): void {
  for (const b of data.bones) {
    if (b.side === 'center') {
      if (b.mirror !== null) out.push(`center bone "${b.id}" must not have a mirror`);
      continue;
    }
    if (b.mirror === null) {
      out.push(`${b.side} bone "${b.id}" is missing its mirror link`);
      continue;
    }
    const twin = ids.get(b.mirror);
    if (!twin) {
      out.push(`bone "${b.id}" mirrors unknown bone "${b.mirror}"`);
    } else if (twin.mirror !== b.id) {
      out.push(`mirror link not involutive: "${b.id}" → "${b.mirror}" → "${twin.mirror}"`);
    } else if (twin.side === b.side) {
      out.push(`mirror pair "${b.id}" ⇄ "${b.mirror}" are both side=${b.side}`);
    }
  }
}

function checkProfiles(data: OntologyData, ids: Map<string, OntologyBone>, out: string[]): void {
  for (const [name, members] of Object.entries(data.profiles)) {
    const set = new Set(members);
    if (set.size !== members.length) out.push(`profile "${name}" has duplicate members`);
    for (const id of members) {
      const b = ids.get(id);
      if (!b) {
        out.push(`profile "${name}" references unknown bone "${id}"`);
        continue;
      }
      // Closure: a profile containing a bone contains its parent (ADR-003 §9).
      if (b.parent !== null && !set.has(b.parent)) {
        out.push(`profile "${name}" contains "${id}" but not its parent "${b.parent}"`);
      }
    }
  }
}

const UNIT_TOL = 1e-6;

function unitError(v: Vec3): boolean {
  return Math.abs(Math.hypot(v.x, v.y, v.z) - 1) > UNIT_TOL;
}

function checkGeometry(data: OntologyData, ids: Map<string, OntologyBone>, out: string[]): void {
  if (unitError(data.mirrorPlane.normal)) {
    out.push('mirrorPlane.normal is not unit length');
  }
  for (const b of data.bones) {
    if (b.tip && b.zeroPoseDirection !== null) {
      out.push(`tip bone "${b.id}" must not declare a zero-pose direction`);
    }
    if (!b.tip && b.zeroPoseDirection === null) {
      // Articulating bones MUST carry the zero-pose reference — auto-targeting
      // and rest-pose conversion measure against it (Codex review, PR #2).
      out.push(`articulating bone "${b.id}" is missing its zero-pose direction`);
    }
    if (!b.tip && b.zeroPoseDirection !== null) {
      const d = b.zeroPoseDirection;
      if (unitError(d)) {
        out.push(`bone "${b.id}" zero-pose direction is not unit length`);
      }
    }
    // The twist/reference axis (ADR-003 §11): articulating bones carry a unit
    // secondary PERPENDICULAR to the primary — otherwise it cannot close the
    // residual-twist DOF it exists to close.
    if (b.tip && b.zeroPoseSecondary !== null) {
      out.push(`tip bone "${b.id}" must not declare a zero-pose secondary`);
    }
    if (!b.tip && b.zeroPoseSecondary === null) {
      out.push(`articulating bone "${b.id}" is missing its zero-pose secondary`);
    }
    if (!b.tip && b.zeroPoseSecondary !== null && b.zeroPoseDirection !== null) {
      const s = b.zeroPoseSecondary;
      const d = b.zeroPoseDirection;
      if (unitError(s)) {
        out.push(`bone "${b.id}" zero-pose secondary is not unit length`);
      }
      const dot = s.x * d.x + s.y * d.y + s.z * d.z;
      if (Math.abs(dot) > UNIT_TOL) {
        out.push(`bone "${b.id}" zero-pose secondary is not perpendicular to its direction (dot=${dot.toFixed(6)})`);
      }
    }
    // Primary child (ADR-003 §11): must actually BE a child of this bone.
    if (b.primaryChild !== null) {
      if (b.tip) out.push(`tip bone "${b.id}" must not declare a primary child`);
      const child = ids.get(b.primaryChild);
      if (!child) {
        out.push(`bone "${b.id}" primary child "${b.primaryChild}" does not exist`);
      } else if (child.parent !== b.id) {
        out.push(`bone "${b.id}" primary child "${b.primaryChild}" is not its child (parent is "${child.parent}")`);
      }
    }
    // Parity convention (ADR-003 §11): exactly the sided bones declare it.
    if (b.side === 'center' && b.parityAxis !== null) {
      out.push(`center bone "${b.id}" must not declare a parity axis`);
    }
    if (b.side !== 'center' && b.parityAxis === null) {
      out.push(`${b.side} bone "${b.id}" is missing its parity axis`);
    }
    if (b.parityAxis !== null && unitError(b.parityAxis)) {
      out.push(`bone "${b.id}" parity axis is not unit length`);
    }
    if (b.tip && b.joint !== null) out.push(`tip bone "${b.id}" must not declare a joint model`);
    // Rest-anchored transport is a rotation-track law — a tip carries no track.
    if (b.tip && b.restAnchoredTransport) {
      out.push(`tip bone "${b.id}" must not declare rest-anchored transport`);
    }
    if (b.tip && b.twistAnchoredTransport) {
      out.push(`tip bone "${b.id}" must not declare twist-anchored transport`);
    }
    // One anchor law per bone: rest anchoring already fixes the twist.
    if (b.restAnchoredTransport && b.twistAnchoredTransport) {
      out.push(`bone "${b.id}" declares both rest-anchored and twist-anchored transport`);
    }
    // A twist anchor needs an axis: the bone's primary child direction.
    if (b.twistAnchoredTransport && b.primaryChild === null) {
      out.push(`bone "${b.id}" declares twist-anchored transport but names no primary child (its axis)`);
    }
    // Riding is a property OF an anchor.
    if (b.anchorChildrenRide && !b.restAnchoredTransport && !b.twistAnchoredTransport) {
      out.push(`bone "${b.id}" declares anchorChildrenRide without an anchor to ride`);
    }
  }
}

/**
 * The only door into an Ontology: schema-validate, then run every semantic
 * integrity check, reporting ALL problems at once (ADR-003 §7, §9).
 */
export function loadOntology(raw: unknown): Ontology {
  const parsed = ontologySchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'Ontology',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  const data = parsed.data;

  const ids = new Map<string, OntologyBone>();
  const problems: string[] = [];
  for (const b of data.bones) {
    if (ids.has(b.id)) problems.push(`duplicate bone id "${b.id}"`);
    ids.set(b.id, b);
  }
  checkHierarchy(data, ids, problems);
  checkMirrors(data, ids, problems);
  checkProfiles(data, ids, problems);
  checkGeometry(data, ids, problems);

  if (problems.length > 0) throw new OntologyIntegrityError(problems);
  return { ...data, byId: ids };
}
